/**
 * Every request the feedback feature makes, and nothing else makes any.
 *
 * The two halves have opposite permissions on the server and that asymmetry is
 * the design (J4): **anybody signed in may submit**, Viewers included, because
 * feedback about the product is not the business data the read-only role
 * guards and a reviewer who cannot report a problem is a reviewer whose
 * problem never gets recorded. **Only an Administrator may read**, on the list,
 * the detail and the export alike.
 *
 * Nothing here edits or deletes an entry. There is no endpoint for it: an
 * entry is said once.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api, expectBody } from "@/api/client"
import type { components } from "@/api/schema"
import { PAGE_SIZE } from "@/lib/constants"

export type FeedbackIn = components["schemas"]["FeedbackIn"]
export type FeedbackCreated = components["schemas"]["FeedbackCreatedOut"]

export const feedbackKeys = {
  all: ["feedback"] as const,
  list: (page: number, ordering: string) => ["feedback", "list", page, ordering] as const,
  entry: (id: number) => ["feedback", "entry", id] as const,
}

/**
 * Send one entry.
 *
 * The list is invalidated on success so an Administrator who submits something
 * and then opens the review screen sees their own entry, rather than a cached
 * page from before it existed.
 */
export function useSubmitFeedback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: FeedbackIn) => {
      const { data, response } = await api.POST("/api/feedback", { body })
      return expectBody(data, response, "sending your feedback")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
    },
  })
}

/** The review list. Administrator-only on the server; the nav hides it too. */
export function useFeedbackList(page: number, ordering: string) {
  return useQuery({
    queryKey: feedbackKeys.list(page, ordering),
    queryFn: async () => {
      const { data, response } = await api.GET("/api/feedback", {
        params: {
          query: {
            page,
            page_size: PAGE_SIZE,
            ordering: ordering as "-created_at",
          },
        },
      })
      return expectBody(data, response, "the feedback people have sent")
    },
    placeholderData: (previous) => previous,
  })
}

/** One entry with the trail the browser recorded beside it. */
export function useFeedbackEntry(id: number) {
  return useQuery({
    queryKey: feedbackKeys.entry(id),
    queryFn: async () => {
      const { data, response } = await api.GET("/api/feedback/{feedback_id}", {
        params: { path: { feedback_id: id } },
      })
      return expectBody(data, response, "this piece of feedback")
    },
    enabled: Number.isInteger(id),
  })
}
